import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CalendarIcon, CreditCardIcon, UserIcon, FileTextIcon, AlertTriangleIcon, Trash2 } from "lucide-react"
import { formatDate } from "@/common/helpers/date.helpers"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { useNegativationDashboardContext } from "../negativation-dashboard/useNegativationDashboardContext"
import { formatCurrency } from "@/common/helpers/mask.helpers"
import Loading from "@/common/components/ui/loading/Loading"

export function NegativationDetails() {
  const { negativation } = useNegativationDashboardContext()
  const isLoading = !negativation || !negativation?._id || !negativation?.negativationId

  // ✅ Mostrar loading enquanto dados não estão prontos
  if (isLoading) {
    return <Loading />
  }
  const paymentDelete = negativation?.idPaymentsDeleted || []

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "CANCELLED":
        return <Badge variant="destructive">Cancelado</Badge>
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }
  []
  return (
    <div className="space-y-6 p-4">
      {/* Financial Information */}
      <Card>
        <CardHeader>
          <CardTitle>Informações Financeiras</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl text font-bold text-foreground">{formatCurrency(negativation?.value)}</div>
              <div className="text-sm text-muted-foreground">Valor Total</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-foreground">{formatCurrency(negativation?.feeValue)}</div>
              <div className="text-sm text-muted-foreground">Taxa</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-foreground">{formatCurrency(negativation?.netValue)}</div>
              <div className="text-sm text-muted-foreground">Valor Líquido</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status and Basic Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCardIcon className="h-5 w-5" />
              Informações Gerais
            </CardTitle>
            {getStatusBadge(negativation?.status)}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-8">
            {/* Locatário à esquerda */}
            <div className="md:w-1/2 space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nome do Locatário</label>
                <div className="flex items-center gap-2 mt-1">
                  <UserIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{negativation?.renterName}</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Número da Fatura</label>
                <div className="flex items-center gap-2 mt-1">
                  <FileTextIcon className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={`https://asaas.com/payment/show/${negativation?.invoiceNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline hover:text-bluePrimary font-mono"
                  >
                    {negativation?.invoiceNumber}
                  </a>
                </div>
              </div>
            </div>
            {/* Informações Gerais à direita */}
            <div className="md:w-1/2 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data da Solicitação</label>
                  <div className="flex items-center gap-2 mt-1">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    <span>{formatDate(negativation?.requestDate)}</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data de Criação</label>
                  <div className="flex items-center gap-2 mt-1">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    <span>{formatDate(negativation?.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>



      {/* Customer and Invoice Info */}

      {negativation?.idPaymentsDeleted && negativation?.idPaymentsDeleted.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <Trash2 className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900">Pagamentos Removidos</h2>
            <div className="text-sm text-muted-foreground">
              {negativation?.idPaymentsDeleted.length} pagamento(s) foram deletados para criar esta negativação.
            </div>
          </div>

          <div className="space-y-3">
            {paymentDelete.map((payment, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-full">
                    <Trash2 className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-orange-900">
                      {payment.description || 'Descrição não disponível'}
                    </h3>
                  </div>
                </div>
                <span className="font-semibold text-orange-900">
                  {formatCurrency(payment.value || 0)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancellation Reason */}
      {negativation?.negativationCancellationReason?.author && (
        <Card className="border-destructive/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangleIcon className="h-5 w-5" />
              Motivo do Cancelamento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Motivo</label>
              <div className="mt-1 p-3 bg-destructive/5 rounded-lg border border-destructive/20">
                <span className="text-base">{negativation?.negativationCancellationReason?.reason}</span>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <label className="font-medium text-muted-foreground">Autor</label>
                <div className="mt-1">{negativation?.negativationCancellationReason?.author}</div>
              </div>
              <div>
                <label className="font-medium text-muted-foreground">Data do Cancelamento</label>
                <div className="mt-1">{formatDate(negativation?.negativationCancellationReason?.createdAt)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
